import {
  ButtonV3,
  DiamondIcon,
  HrvIcon,
  RingBatteryIcon,
  TemperatureIcon,
} from '@jouzen/ecom-components';
import { cx } from 'class-variance-authority';
import { useInView } from 'framer-motion';
import { usePathname } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { useRef } from 'react';

import { EventType } from '@/analytics/types';
import Image from '@/app/components/Image';
import slugifyString from '@/utils/slugifyString';

import TOCEntry from './TOCEntry';

export interface ProductBreakthroughItemsProps {
  readonly shopButton?: boolean | undefined;
}

const ProductBreakthroughItems = ({
  shopButton = false,
}: ProductBreakthroughItemsProps) => {
  const path = usePathname();
  const t = useTranslations();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(wrapperRef, { amount: 0.75 });

  const handleAnalytics = async () => {
    void window.ouraAnalytics.track(EventType.CTAClicked, {
      cta: slugifyString(t('header_shop_now')),
      location: 'productbreakthrough_module',
      module: 'productbreakthrough_module',
      path,
    });
  };

  return (
    <div ref={wrapperRef} className="col-main">
      <div className="relative">
        <div className="mx-auto max-w-[674px] lg:w-[30%] xxl:w-[54%]">
          <Image
            priority
            src="product/simple/pdp-tldr-silver-lights-horizon@2x"
            format="png"
            width={674}
            height={612}
            sizes="(min-width: 600px) 674px, (min-width: 448px) 552px, (min-width: 360px) 400px, 312px"
            localizedAlt="pdp_horizon_breakthrough_image_alt"
          />
        </div>

        <div className="pt-8 md:flex md:flex-wrap xxl:block">
          <div
            className={cx(
              'py-4 md:w-1/2 md:p-4 lg:w-1/4 xxl:absolute xxl:top-[65%] xxl:right-0 xxl:w-auto xxl:max-w-xs xxl:p-0 xxl:pl-4',
              'xxl:before:absolute xxl:before:top-[3.75rem] xxl:before:right-0 xxl:before:left-auto xxl:before:inline-block xxl:before:bg-white xxl:before:content-[""]',
              'xxl:before:right-[calc(100%+13rem)] xxl:before:h-[5px] xxl:before:w-[5px] xxl:before:-translate-y-1/2 xxl:before:rounded-full xxl:before:opacity-0 xxl:before:will-change-[opacity]',
              'xxl:after:absolute xxl:after:top-[3.75rem] xxl:after:right-full xxl:after:inline-block xxl:after:bg-white xxl:after:content-[""]',
              'xxl:after:-left-52 xxl:after:h-px xxl:after:w-0 xxl:after:transition-all xxl:after:duration-300 xxl:after:will-change-[width]',
              {
                'xxl:before:opacity-100 xxl:after:w-48': isInView,
              },
            )}
          >
            <TOCEntry
              icon={
                <HrvIcon
                  className="rounded-full border border-white/20 p-2"
                  width={36}
                  height={36}
                />
              }
              label={t('pdp_horizon_breakthrough_accuracy_title')}
              summary={t('pdp_horizon_breakthrough_accuracy_description')}
            />
          </div>
          <div
            className={cx(
              'py-4 md:w-1/2 md:p-4 lg:w-1/4 xxl:absolute xxl:top-[58%] xxl:left-0 xxl:w-auto xxl:max-w-xs xxl:p-0 xxl:pr-4',
              'xxl:before:absolute xxl:before:top-[3.75rem] xxl:before:right-0 xxl:before:inline-block xxl:before:bg-white xxl:before:content-[""]',
              'xxl:before:left-[calc(100%+14rem)] xxl:before:h-[5px] xxl:before:w-[5px] xxl:before:-translate-y-1/2 xxl:before:rounded-full xxl:before:opacity-0 xxl:before:will-change-[opacity]',
              'xxl:after:absolute xxl:after:top-[3.75rem] xxl:after:left-auto xxl:after:inline-block xxl:after:bg-white xxl:after:content-[""]',
              'xxl:after:-right-56 xxl:after:h-px xxl:after:w-0 xxl:after:transition-all xxl:after:duration-300 xxl:after:will-change-[width]',
              {
                'xxl:before:opacity-100 xxl:after:w-56': isInView,
              },
            )}
          >
            <TOCEntry
              icon={
                <RingBatteryIcon
                  className="rounded-full border border-white/20 p-2"
                  width={36}
                  height={36}
                />
              }
              label={t('pdp_horizon_breakthrough_battery_title')}
              summary={t('pdp_horizon_breakthrough_battery_description')}
            />
          </div>
          <div
            className={cx(
              'py-4 md:w-1/2 md:p-4 lg:w-1/4 xxl:absolute xxl:top-[28%] xxl:left-0 xxl:w-auto xxl:max-w-xs xxl:p-0 xxl:pr-4',
              'xxl:before:absolute xxl:before:top-[3.75rem] xxl:before:right-0 xxl:before:inline-block xxl:before:bg-white xxl:before:content-[""]',
              'xxl:before:left-[calc(100%+10rem)] xxl:before:h-[5px] xxl:before:w-[5px] xxl:before:-translate-y-1/2 xxl:before:rounded-full xxl:before:opacity-0 xxl:before:will-change-[opacity]',
              'xxl:after:absolute xxl:after:top-[3.75rem] xxl:after:left-auto xxl:after:inline-block xxl:after:bg-white xxl:after:content-[""]',
              'xxl:after:-right-40 xxl:after:h-px xxl:after:w-0 xxl:after:transition-all xxl:after:duration-300 xxl:after:will-change-[width]',
              {
                'xxl:before:opacity-100 xxl:after:w-36': isInView,
              },
            )}
          >
            <TOCEntry
              icon={
                <TemperatureIcon
                  className="rounded-full border border-white/20 p-2"
                  width={36}
                  height={36}
                />
              }
              label={t('pdp_horizon_breakthrough_sensors_title')}
              summary={t('pdp_horizon_breakthrough_sensors_description')}
            />
          </div>
          <div
            className={cx(
              'py-4 md:w-1/2 md:p-4 lg:w-1/4 xxl:absolute xxl:top-[24%] xxl:right-0 xxl:w-auto xxl:max-w-xs xxl:p-0 xxl:pl-4',
              'xxl:before:absolute xxl:before:top-[3.75rem] xxl:before:right-0 xxl:before:left-auto xxl:before:inline-block xxl:before:bg-white xxl:before:content-[""]',
              'xxl:before:right-[calc(100%+19rem)] xxl:before:h-[5px] xxl:before:w-[5px] xxl:before:-translate-y-1/2 xxl:before:rounded-full xxl:before:opacity-0 xxl:before:will-change-[opacity]',
              'xxl:after:absolute xxl:after:top-[3.75rem] xxl:after:right-full xxl:after:inline-block xxl:after:bg-white xxl:after:content-[""]',
              'xxl:after:-left-[19rem] xxl:after:h-px xxl:after:w-0 xxl:after:transition-all xxl:after:duration-300 xxl:after:will-change-[width]',
              {
                'xxl:before:opacity-100 xxl:after:w-[18rem]': isInView,
              },
            )}
          >
            <TOCEntry
              icon={
                <DiamondIcon
                  className="rounded-full border border-white/20 p-2"
                  width={36}
                  height={36}
                />
              }
              label={t('pdp_horizon_breakthrough_design_title')}
              summary={t('pdp_horizon_breakthrough_design_description')}
            />
          </div>
        </div>
      </div>
      {shopButton && (
        <div className="m-auto mt-2 text-center md:mt-16">
          <ButtonV3
            variant="secondary-light"
            href="/product/rings"
            onClick={handleAnalytics}
          >
            {t('header_shop_now')}
          </ButtonV3>
        </div>
      )}
    </div>
  );
};

export default ProductBreakthroughItems;
